import axios from 'axios';

// Debug mode to log requests and responses
const DEBUG = true;

// Birden fazla yönlendirmeyi engellemek için flag
let isRedirecting = false;

// Create an axios instance with default config
const api = axios.create({
  baseURL: 'https://bank.wells.finance/api/v1',
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true, // Critical for sending cookies with cross-origin requests
});

// Add CSRF token to requests that need it
api.interceptors.request.use(
  (config) => {
    // Debug log
    if (DEBUG) {
      console.log(`API Request: ${config.method.toUpperCase()} ${config.url}`, {
        withCredentials: config.withCredentials,
        hasToken: !!localStorage.getItem('csrf_token')
      });
    }
    
    // Eğer zaten yönlendirme yapılıyorsa, auth dışındaki istekleri engelle
    if (isRedirecting && !config.url.includes('/auth/')) {
      console.log('İstek engellendi - Authentication yönlendirmesi devam ediyor');
      return Promise.reject(new Error('Authentication yönlendirmesi devam ediyor'));
    }
    
    // Only add CSRF token to non-GET requests
    if (config.method !== 'get') {
      const csrfToken = localStorage.getItem('csrf_token');
      if (csrfToken) {
        // Create a new data object if needed
        if (!config.data) {
          config.data = {};
        }
        
        // If data is a string, try to parse it
        if (typeof config.data === 'string') {
          try {
            config.data = JSON.parse(config.data);
          } catch (e) {
            console.error('Error parsing request data:', e);
          }
        }
        
        // Add token to data if it's an object
        if (typeof config.data === 'object') {
          config.data.csrf_token = csrfToken;
        }
      }
    }
    
    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Store CSRF token from responses and handle authentication errors
api.interceptors.response.use(
  (response) => {
    // Debug log
    if (DEBUG) {
      console.log(`API Response: ${response.config.method.toUpperCase()} ${response.config.url}`, {
        status: response.status,
        successValue: response.data?.success,
        dataKeys: response.data?.data ? Object.keys(response.data.data) : 'no data'
      });
    }
    
    if (response.data?.data?.csrf_token) {
      localStorage.setItem('csrf_token', response.data.data.csrf_token);
      if (DEBUG) console.log('CSRF Token stored:', response.data.data.csrf_token);
    }
    
    return response;
  },
  (error) => {
    // Enhanced error handling
    console.error('API Error:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      data: error.response?.data,
    });
    
    // Tüm authentication hata senaryolarını kontrol et
    const isAuthError = 
      error.response?.status === 401 ||
      error.response?.status === 403 ||
      error.response?.data?.message === "Authentication required" ||
      error.response?.data?.message === "Session expired" ||
      error.response?.data?.message === "Invalid session" ||
      error.response?.data?.error === "Unauthorized" ||
      (error.response?.data?.success === false && 
       error.response?.data?.message?.toLowerCase().includes('auth'));
    
    // Auth endpoint'lerini atla (sonsuz döngüyü önle)
    const isAuthEndpoint = error.config?.url?.includes('/auth/');
    
    // Eğer auth hatası varsa ve henüz yönlendirme yapılmamışsa
    if (isAuthError && !isAuthEndpoint && !isRedirecting) {
      console.error('Authentication hatası tespit edildi. Login sayfasına yönlendiriliyor...');
      
      // Yönlendirme flag'ini aktif et
      isRedirecting = true;
      
      // Tüm auth verilerini temizle
      localStorage.removeItem('isAuthenticated');
      localStorage.removeItem('currentUser');
      localStorage.removeItem('csrf_token');
      
      // Diğer component'lere haber ver
      window.dispatchEvent(new CustomEvent('auth-error', {
        detail: {
          status: error.response?.status,
          message: error.response?.data?.message,
          timestamp: Date.now()
        }
      }));
      
      // Login sayfasına yönlendir
      setTimeout(() => {
        window.location.href = '/login';
        // 2 saniye sonra flag'i sıfırla
        setTimeout(() => {
          isRedirecting = false;
        }, 2000);
      }, 100);
      
      // Özel bir hata döndür
      return Promise.reject({
        ...error,
        handled: true,
        authError: true
      });
    }
    
    return Promise.reject(error);
  }
);

// Authentication APIs
export const authAPI = {
  // Get current user and authentication status
  getUser: () => api.get('/auth/user'),
  
  // Login with email to get OTP
  login: (email) => api.post('/auth/login', { email }),
  
  // Verify OTP code
  verifyOTP: (email, otp) => api.post('/auth/verify', { email, otp }),
  
  // Complete registration/profile
  register: (data) => api.post('/auth/register', data),
  
  // Logout
  logout: () => api.get('/auth/logout'),
};

// Portfolio APIs
export const portfolioAPI = {
  getSummary: () => api.get('/portfolio/summary'),
  getDashboard: () => api.get('/portfolio/dashboard'),
  getPositions: () => api.get('/portfolio/positions'),
};

// Performance APIs
export const performanceAPI = {
  getData: (filters) => api.post('/performance/data', filters),
  getModelDetails: (modelId, filters) => api.get(`/performance/model?model_id=${modelId}`, { params: filters }),
  getSymbols: () => api.get('/performance/symbols'),
  getSessions: () => api.get('/performance/sessions'),
};

// Settings APIs
export const settingsAPI = {
  getSettings: () => api.get('/settings/get'),
  updateSettings: (settings) => api.post('/settings/update', settings),
  checkConnection: () => api.post('/settings/check_connection'),
  disconnect: () => api.post('/settings/disconnect'),
};

// Trade APIs
export const tradeAPI = {
  closeAllPositions: () => api.post('/trade/close-all'),
  getLogs: (page = 1, limit = 25, search = '') => {
    const params = { page, limit };
    if (search) {
      params.search = search;
    }
    return api.get('/trade/logs', { params });
  },
  getTradeDetails: (id) => api.get(`/trade/details/${id}`),
};

// Market APIs
export const marketAPI = {
  getSymbols: () => api.get('/market/symbols'),
  getPrices: (symbols) => api.get('/market/prices', { params: { symbols } }),
  getStatus: () => api.get('/market/status'),
  getDetails: (symbol) => api.get('/market/details', { params: { symbol } }),
};

// Telegram APIs
export const telegramAPI = {
  getLogs: (limit = 10) => api.get('/telegram/logs', { params: { limit } }),
  getActivities: (limit = 5) => api.get('/telegram/activities', { params: { limit } }),
};

export default {
  auth: authAPI,
  portfolio: portfolioAPI,
  performance: performanceAPI,
  settings: settingsAPI,
  trade: tradeAPI,
  market: marketAPI,
  telegram: telegramAPI,
};